/**
 * APC Dashboard
 *
 * Supervising physician view of their supervised APCs' EQuIP performance.
 * Reuses ALL leadership UI components (LeaderProfileBar, TeamMeasureChart,
 * ProviderHeatmap) since the backend now returns LeadershipAggregation shape.
 *
 * Key differences from LeadershipProgressDetails:
 * - Calls getSupervisedApcs instead of getLeadershipAggregation
 * - canSeeAwards is always false — physicians never see dollar values
 * - No IncentiveProjectionCard
 * - Uses selectedPhysicianNpi (from EquipScopeContext) for admin impersonation
 * - Non-admin users with no APC supervision get an informational empty state
 *
 * Role context:
 * - Intended for equip.read physicians with entries in cqm_apc_physician_mapping
 * - If the user has no APC supervision (context.hasApcSupervision === false),
 *   shows an informational empty state rather than an error
 */

import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { BarChart3, Info } from 'lucide-react'
import { LeaderProfileBar } from '@/views/equip/leadership/components/LeaderProfileBar'
import { ProviderHeatmap } from '@/views/equip/leadership/components/ProviderHeatmap'
import { TeamMeasureChart } from '@/views/equip/leadership/components/TeamMeasureChart'
import { useCurrentQuarter } from '@/hooks/equip/useCurrentQuarter'
import { useMyContext } from '@/hooks/equip/useEquipPerformance'
import { equipApi } from '@/services/equipApi'
import { useEquipScopeOptional } from '@/views/equip/context/EquipScopeContext'
import type { LeadershipAggregation } from '@/lib/equip-types'

export function APCDashboard() {
  // Current quarter from backend API (cached, gated by EquipScopeContext loading)
  const currentQuarter = useCurrentQuarter()

  // State — keep selectedQuarter (object format), filter controls
  const [selectedQuarter, setSelectedQuarter] = useState({ year: currentQuarter.year, quarter: currentQuarter.quarter })
  const [aggregation, setAggregation] = useState<LeadershipAggregation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProviderType, setSelectedProviderType] = useState<string>('all')
  const [selectedCareTeam, setSelectedCareTeam] = useState<string>('all')
  const [sortColumn, setSortColumn] = useState<string>('providerName')
  const [eligibleOnly, setEligibleOnly] = useState(false)
  const [activeOnly, setActiveOnly] = useState(true)

  // Determine if the selected quarter is the current quarter
  const isCurrentQuarter =
    selectedQuarter.year === currentQuarter.year &&
    selectedQuarter.quarter === currentQuarter.quarter

  // User context — determines whether this user has APC supervision at all
  const contextQuery = useMyContext()
  const hasApcSupervision = contextQuery.data?.hasApcSupervision ?? false
  const contextLoading = contextQuery.isPending

  // Admin physician impersonation scope
  const equipScope = useEquipScopeOptional()
  const isAdmin = equipScope?.isAdmin ?? false
  const selectedPhysicianNpi = equipScope?.selectedPhysicianNpi ?? null

  // Physicians never see dollar values — canSeeAwards is always false here
  const canSeeAwards = false

  // Fetch supervised APC aggregation whenever quarter or selected physician changes
  useEffect(() => {
    // Don't fetch until context is resolved
    if (contextLoading) return
    // Non-admin: if user has no supervision, skip the fetch
    if (!isAdmin && !hasApcSupervision) {
      setIsLoading(false)
      return
    }
    // Admin with no physician selected: show prompt, skip fetch
    if (isAdmin && selectedPhysicianNpi === null) {
      setIsLoading(false)
      setAggregation(null)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    equipApi.dashboard
      .getSupervisedApcs({
        year: selectedQuarter.year,
        quarter: selectedQuarter.quarter,
        ...(isAdmin && selectedPhysicianNpi !== null ? { physicianNpi: selectedPhysicianNpi } : {}),
      })
      .then((data) => {
        if (!cancelled) {
          setAggregation(data)
          setIsLoading(false)
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          // 404 means no supervised APCs — treat as empty state, not error
          if (err.message?.includes('404') || err.message?.includes('No supervised')) {
            setAggregation(null)
          } else {
            setError(err.message || 'Failed to load APC performance data')
          }
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedQuarter.year, selectedQuarter.quarter, hasApcSupervision, contextLoading, isAdmin, selectedPhysicianNpi])

  // Handle quarter change from heatmap slicer
  const handleQuarterChange = (quarterStr: string) => {
    if (quarterStr === 'all') {
      setSelectedQuarter({ year: currentQuarter.year, quarter: currentQuarter.quarter })
      return
    }
    const [year, quarterPart] = quarterStr.split('-Q')
    setSelectedQuarter({
      year: parseInt(year, 10),
      quarter: parseInt(quarterPart, 10),
    })
  }

  // Apply provider type and eligibility filters
  const filteredProviders = useMemo(() => {
    if (!aggregation) return []

    let filtered = aggregation.providers

    if (selectedProviderType !== 'all') {
      filtered = filtered.filter((p) => p.bonusEligibleType === selectedProviderType)
    }

    if (eligibleOnly) {
      filtered = filtered.filter((p) =>
        isCurrentQuarter ? p.eligibleAtQuarterStart : p.eligibleAtQuarterEnd
      )
    }

    if (activeOnly) {
      filtered = filtered.filter((p) => !p.isInactiveInQuarter)
    }

    return filtered
  }, [aggregation, selectedProviderType, eligibleOnly, isCurrentQuarter, activeOnly])

  // Recalculate summary metrics from filtered providers.
  // Terminated-in-quarter providers are ALWAYS excluded from rollup values
  // regardless of the activeOnly toggle — they are never meaningful for aggregation.
  const filteredAggregation: LeadershipAggregation | null = useMemo(() => {
    if (!aggregation) return null
    // When a scoped manager's identity can't be resolved, the backend sets
    // managerIdentityUnresolved AND returns zero providers. Preserve the
    // aggregation (with its flag) so LeaderProfileBar renders the "Leader not
    // identified" warning instead of falling through to the generic no-data
    // card — otherwise the true-positive warning is unreachable (audit #1).
    if (filteredProviders.length === 0) {
      return aggregation.managerIdentityUnresolved ? aggregation : null
    }

    // Rollup excludes terminated providers; display count reflects what is shown
    const rollupProviders = filteredProviders.filter((p) => !p.isInactiveInQuarter)

    const atRiskCount = rollupProviders.filter(
      (p) => p.totalPercentage !== null && (p.totalPercentage * 100) < 89
    ).length

    const avgTotalPercentage =
      rollupProviders.length > 0
        ? rollupProviders.reduce((sum, p) => sum + (p.totalPercentage ?? 0), 0) /
          rollupProviders.length
        : 0

    const totalPatients = rollupProviders.reduce(
      (sum, p) => sum + (p.totalPatientCount ?? 0),
      0
    )

    const totalAcoPatients = rollupProviders.reduce(
      (sum, p) => sum + (p.acoPatientCount ?? 0),
      0
    )

    return {
      ...aggregation,
      providers: filteredProviders,
      totalProviders: filteredProviders.length,
      atRiskCount,
      avgTotalPercentage,
      // Financial aggregates stay null throughout
      avgOutcomePercentage: null,
      avgConsistencyPercentage: null,
      totalProjectedAwards: null,
      totalPatients,
      totalAcoPatients,
    }
  }, [aggregation, filteredProviders])

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedProviderType('all')
    setActiveOnly(true)
  }

  // ── Non-admin: no supervision empty state ─────────────────────────────────
  if (!contextLoading && !isAdmin && !hasApcSupervision) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-3"
      >
        <div>
          <h1 className="text-2xl font-bold">Supervising Physician</h1>
          <p className="text-muted-foreground mt-1">
            Supervised APC performance overview
          </p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Info className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">No supervised APCs found</p>
            <p className="text-sm text-muted-foreground max-w-sm">
              Your account is not mapped as a collaborating physician for any APCs
              in the current EQuIP program. Contact your administrator if you believe
              this is incorrect.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  // ── Admin: no physician selected yet ──────────────────────────────────────
  if (!contextLoading && isAdmin && selectedPhysicianNpi === null) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-3"
      >
        <div>
          <h1 className="text-2xl font-bold">Supervising Physician</h1>
          <p className="text-muted-foreground mt-1">
            Supervised APC performance overview
          </p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Info className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Select a supervising physician</p>
            <p className="text-sm text-muted-foreground max-w-sm">
              Use the physician dropdown in the header to select a supervising physician
              and view their supervised APCs.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4 mb-3">
        <div>
          <h1 className="text-2xl font-bold">Supervising Physician</h1>
          <p className="text-muted-foreground mt-1">
            Performance overview for supervised APCs
          </p>
        </div>
      </div>

      {/* Error state — different treatment for current vs historical quarter */}
      {error && (
        isCurrentQuarter ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold">No Data for Q{selectedQuarter.quarter} {selectedQuarter.year}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Historical data will be available here once the quarter is complete.
                </p>
              </div>
              <Button onClick={() => setSelectedQuarter({ year: currentQuarter.year, quarter: currentQuarter.quarter })}>
                View Current Quarter
              </Button>
            </CardContent>
          </Card>
        )
      )}

      {/* Leader Profile Bar — physician card, always visible below page header */}
      <LeaderProfileBar
        aggregation={filteredAggregation}
        isLoading={isLoading}
        isCurrentQuarter={isCurrentQuarter}
        canSeeAwards={canSeeAwards}
      />

      {/* Team Measure Chart */}
      <TeamMeasureChart
        providers={filteredProviders}
        isLoading={isLoading}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
      />

      {/* Provider Heatmap — filter controls (quarter, type, eligible) live inside the card header */}
      <ProviderHeatmap
        providers={filteredProviders}
        isLoading={isLoading}
        sortColumn={sortColumn}
        onSortChange={setSortColumn}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
        selectedQuarter={`${selectedQuarter.year}-Q${selectedQuarter.quarter}`}
        onQuarterChange={handleQuarterChange}
        selectedProviderType={selectedProviderType}
        onProviderTypeChange={setSelectedProviderType}
        selectedCareTeam={selectedCareTeam}
        onCareTeamChange={setSelectedCareTeam}
        eligibleOnly={eligibleOnly}
        onEligibleOnlyChange={setEligibleOnly}
        activeOnly={activeOnly}
        onActiveOnlyChange={setActiveOnly}
        allProviders={aggregation?.providers ?? []}
        onClearFilters={handleClearFilters}
        canSeeAwards={canSeeAwards}
      />
    </div>
  )
}
